(() => {
  const TARGET_SELECTORS = [
    '[data-size-guide-target="simonis-860"]',
    '[data-size-guide-target="championship-invitational"]'
  ];
  const GUIDE_SELECTOR = '[data-size-guide]';
  const HAND_SELECTOR = '[data-size-guide-hand]';
  const SIZE_RAIL_SELECTOR = '[data-simonis-size-rail]';
  const SIZE_OPTION_SELECTOR = '[data-simonis-size-option]';
  const ACTIVE_CLASS = 'is-visible';
  const FINISHED_CLASS = 'is-finished';
  const MOBILE_QUERY = '(max-width: 749px)';
  const START_DELAY_MS = 300;
  const DESKTOP_FINISH_DELAY_MS = 3500;
  const MOBILE_FINISH_DELAY_MS = 1800;
  const RIGHT_EDGE_GAP_PX = 10;
  const GUIDE_LEFT_INSET_PX = 4;

  if (window.__simonisSizeGuideBound) return;
  window.__simonisSizeGuideBound = true;

  const mediaQuery = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
  const boundCards = new WeakSet();
  const mobileTimers = new WeakMap();
  let desktopObserver = null;
  let mobileObserver = null;
  let desktopStarted = false;
  let desktopFinished = false;
  let desktopShowTimer = null;
  let desktopHideTimer = null;

  function isMobile() {
    return mediaQuery ? mediaQuery.matches : window.innerWidth <= 749;
  }

  function getCards() {
    return TARGET_SELECTORS
      .map((selector) => document.querySelector(selector))
      .filter(Boolean);
  }

  function getGuide(card) {
    return card ? card.querySelector(GUIDE_SELECTOR) : null;
  }

  function setGuideVisibility(guide, visible) {
    if (!guide) return;
    guide.classList.toggle(ACTIVE_CLASS, visible);
    guide.classList.toggle(FINISHED_CLASS, !visible);
    guide.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  function setSwipeDistance(card) {
    const rail = card ? card.querySelector(SIZE_RAIL_SELECTOR) : null;
    const guide = getGuide(card);
    const hand = guide ? guide.querySelector(HAND_SELECTOR) : null;
    if (!rail || !guide || !hand) return 0;

    const railWidth = rail.getBoundingClientRect().width;
    const handWidth = hand.getBoundingClientRect().width || hand.offsetWidth || 44;
    const distance = Math.max(
      0,
      Math.floor(railWidth - handWidth - RIGHT_EDGE_GAP_PX - GUIDE_LEFT_INSET_PX)
    );

    guide.style.setProperty('--size-guide-swipe-distance', `${distance}px`);
    guide.dataset.sizeGuideSwipeDistance = String(distance);
    return distance;
  }

  function finishMobileCard(card) {
    const timers = mobileTimers.get(card);
    if (timers) {
      window.clearTimeout(timers.showTimer);
      window.clearTimeout(timers.hideTimer);
      mobileTimers.delete(card);
    }

    card.dataset.sizeGuideFinished = 'true';
    setGuideVisibility(getGuide(card), false);
  }

  function startMobileCard(card) {
    const guide = getGuide(card);
    if (
      !guide ||
      !isMobile() ||
      card.dataset.sizeGuideStarted === 'true' ||
      card.dataset.sizeGuideFinished === 'true'
    ) return;

    card.dataset.sizeGuideStarted = 'true';
    const timers = { showTimer: null, hideTimer: null };
    mobileTimers.set(card, timers);

    timers.showTimer = window.setTimeout(() => {
      if (card.dataset.sizeGuideFinished === 'true' || !isMobile()) return;
      setSwipeDistance(card);
      guide.classList.remove(FINISHED_CLASS);
      guide.classList.add(ACTIVE_CLASS);
      guide.setAttribute('aria-hidden', 'false');
      timers.hideTimer = window.setTimeout(
        () => finishMobileCard(card),
        MOBILE_FINISH_DELAY_MS
      );
    }, START_DELAY_MS);
  }

  function hideDesktopGuides() {
    desktopFinished = true;
    window.clearTimeout(desktopShowTimer);
    window.clearTimeout(desktopHideTimer);
    getCards().forEach((card) => setGuideVisibility(getGuide(card), false));
  }

  function startDesktopGuides() {
    const cards = getCards();
    const guides = cards.map(getGuide).filter(Boolean);
    if (
      isMobile() ||
      desktopStarted ||
      desktopFinished ||
      cards.length !== TARGET_SELECTORS.length ||
      guides.length !== TARGET_SELECTORS.length
    ) return;

    desktopStarted = true;
    desktopShowTimer = window.setTimeout(() => {
      if (desktopFinished || isMobile()) return;
      guides.forEach((guide) => {
        guide.classList.remove(FINISHED_CLASS);
        guide.classList.add(ACTIVE_CLASS);
        guide.setAttribute('aria-hidden', 'false');
      });
      desktopHideTimer = window.setTimeout(hideDesktopGuides, DESKTOP_FINISH_DELAY_MS);
    }, START_DELAY_MS);
  }

  function bindDismissal(card) {
    if (boundCards.has(card)) return;
    boundCards.add(card);

    const dismiss = (event) => {
      if (!event.target.closest(SIZE_OPTION_SELECTOR)) return;
      if (isMobile()) {
        finishMobileCard(card);
      } else {
        hideDesktopGuides();
      }
    };

    card.addEventListener('pointerdown', dismiss, { passive: true });
    card.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') dismiss(event);
    });
  }

  function initializeMobile(cards) {
    if (!('IntersectionObserver' in window)) {
      cards.forEach(startMobileCard);
      return;
    }

    if (!mobileObserver) {
      mobileObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          mobileObserver.unobserve(entry.target);
          startMobileCard(entry.target);
        });
      }, { rootMargin: '0px', threshold: 0.2 });
    }

    cards.forEach((card) => {
      if (
        card.dataset.sizeGuideStarted !== 'true' &&
        card.dataset.sizeGuideFinished !== 'true'
      ) mobileObserver.observe(card);
    });
  }

  function initializeDesktop(cards) {
    if (desktopStarted || desktopFinished) return;

    if ('IntersectionObserver' in window) {
      if (desktopObserver) desktopObserver.disconnect();
      const trigger = cards[0].closest('[data-cloth-product-rows]') || cards[0];
      desktopObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        desktopObserver.disconnect();
        startDesktopGuides();
      }, { rootMargin: '0px', threshold: 0.2 });
      desktopObserver.observe(trigger);
      return;
    }

    startDesktopGuides();
  }

  function initialize() {
    const cards = getCards();
    if (cards.length !== TARGET_SELECTORS.length) return;
    cards.forEach(bindDismissal);

    if (isMobile()) {
      initializeMobile(cards);
    } else {
      initializeDesktop(cards);
    }
  }

  function handleViewportChange() {
    if (desktopObserver) desktopObserver.disconnect();
    if (mobileObserver) mobileObserver.disconnect();
    desktopObserver = null;
    mobileObserver = null;
    initialize();
  }

  if (mediaQuery) {
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleViewportChange);
    }
  }

  window.addEventListener('resize', () => {
    if (!isMobile()) return;
    getCards().forEach((card) => {
      const guide = getGuide(card);
      if (guide && guide.classList.contains(ACTIVE_CLASS)) setSwipeDistance(card);
    });
  }, { passive: true });

  document.addEventListener('DOMContentLoaded', initialize);
  document.addEventListener('shopify:section:load', initialize);
  if (document.readyState !== 'loading') initialize();
})();
